import { db, auth } from "../../services/firebase";
import {
  collection,
  doc,
  query,
  where,
  getDocs,
  getDoc,
  updateDoc,
  arrayUnion,
  limit
} from "firebase/firestore";

function normalizeQueryValue(value) {
  return String(value || "").trim().toLowerCase();
}

function serviceMatchesContinent(service, continent) {
  const normalizedContinent = normalizeQueryValue(continent);
  const serviceContinents = Array.isArray(service.continent)
    ? service.continent
    : [service.continent];

  return serviceContinents
    .filter(Boolean)
    .map(normalizeQueryValue)
    .includes(normalizedContinent);
}

export async function getServicesByCategoryClient({
  continent,
  city,
  category,
  debugLabel = null,
  legacyLocationFallback = false
}) {
  if (!continent || !city) {
    throw new Error("continent and city are required");
  }
  const startedAt = performance.now();
  const normalizedCity = normalizeQueryValue(city);
  const normalizedContinent = normalizeQueryValue(continent);

  const servicesArray = [];

  const servicesRef = collection(db, "services");

  const serviceCitiesQuery = query(
    servicesRef,
    where("serviceCities", "array-contains", normalizedCity),
    where("category", "==", category),
    where("approved", "==", true),
    where("disabled", "==", false),
  );

  const serviceCitiesSnapshot = await getDocs(serviceCitiesQuery);

  serviceCitiesSnapshot.forEach((doc) => {
    const service = { id: doc.id, ...doc.data() };
    if (serviceMatchesContinent(service, normalizedContinent)) {
      servicesArray.push(service);
    }
  });

  const legacyCityQuery = query(
    servicesRef,
    where("continent", "array-contains", normalizedContinent),
    where("category", "==", category),
    where("city", "==", normalizedCity),
    where("approved", "==", true),
    where("disabled", "==", false),
  );

  const legacyCitySnapshot = await getDocs(legacyCityQuery);

  legacyCitySnapshot.forEach((doc) => {
    servicesArray.push({ id: doc.id, ...doc.data() });
  });

  let legacyLocationSnapshotSize = 0;

  if (legacyLocationFallback) {
    const legacyLocationQuery = query(
      servicesRef,
      where("continent", "array-contains", normalizedContinent),
      where("category", "==", category),
      where("approved", "==", true),
      where("disabled", "==", false),
    );

    const legacyLocationSnapshot = await getDocs(legacyLocationQuery);
    legacyLocationSnapshotSize = legacyLocationSnapshot.size;

    legacyLocationSnapshot.forEach((doc) => {
      const data = doc.data();
      if (
        Array.isArray(data.locations) &&
        data.locations.some(
          (loc) => normalizeQueryValue(loc?.city) === normalizedCity
        )
      ) {
        servicesArray.push({ id: doc.id, ...data });
      }
    });
  }

  const uniqueServices = Array.from(
    new Map(servicesArray.map((service) => [service.id, service])).values()
  );

  if (debugLabel) {
    console.groupCollapsed(`[FirestoreReads:${debugLabel}] getServicesByCategory ${category}`);
    console.table({
      continent,
      city,
      category,
      serviceCitiesQueryReads: serviceCitiesSnapshot.size,
      legacyCityQueryReads: legacyCitySnapshot.size,
      legacyLocationFallbackReads: legacyLocationSnapshotSize,
      estimatedTotalReads: serviceCitiesSnapshot.size + legacyCitySnapshot.size + legacyLocationSnapshotSize,
      returnedServices: uniqueServices.length,
      duplicateServiceDocs: servicesArray.length - uniqueServices.length,
      legacyLocationFallback,
      elapsedMs: Math.round(performance.now() - startedAt)
    });
    console.groupEnd();
  }

  return { services: uniqueServices };
}

export async function getServiceWithItemsClient({
  serviceId,
  includeItems = true,
  itemLimit = null,
  debugLabel = null
}) {
  if (!serviceId) {
    throw new Error("Service ID is required.");
  }

  const startedAt = performance.now();
  const serviceRef = doc(db, "services", serviceId);
  const serviceSnap = await getDoc(serviceRef);

  if (!serviceSnap.exists()) {
    throw new Error("Service not found.");
  }

  const serviceData = serviceSnap.data();

  let items = [];
  let itemReadCount = 0;

  if (includeItems) {
    const itemsColRef = collection(serviceRef, "servicesAndPrices");
    const itemsQuery = itemLimit ? query(itemsColRef, limit(itemLimit)) : itemsColRef;
    const itemsSnap = await getDocs(itemsQuery);
    itemReadCount = itemsSnap.size;
    items = itemsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  // determine if current user is provider (purely convenience, not security)
  const currentUid = auth.currentUser?.uid || null;
  const isProvider = currentUid && serviceData.providerId === currentUid;

  if (debugLabel) {
    const estimatedReads = 1 + itemReadCount;
    console.groupCollapsed(`[FirestoreReads:${debugLabel}] getServiceWithItems ${serviceId}`);
    console.table({
      serviceDocReads: 1,
      itemDocReads: itemReadCount,
      estimatedTotalReads: estimatedReads,
      includeItems,
      itemLimit: itemLimit || "none",
      elapsedMs: Math.round(performance.now() - startedAt)
    });
    console.groupEnd();
  }

  return {
    service: { id: serviceSnap.id, ...serviceData },
    items,
    isProvider,
  };
}

export async function getServicePublicInfoClient(serviceId) {
  if (!serviceId) {
    throw new Error("serviceId is required");
  }

  const serviceRef = doc(db, "services", serviceId);
  const snap = await getDoc(serviceRef);

  if (!snap.exists()) {
    throw new Error("Service not found");
  }

  const data = snap.data();

  return data;

  // Return ONLY fields you are okay exposing publicly
  // return {
  //   id: snap.id,
  //   name: data.name,
  //   category: data.category,
  //   description: data.description || "",
  //   locations: data.locations || [],
  //   logo: data.logo || null,
  //   banner: data.banner || null,
  //   businessImages: data.businessImages || [],
  //   orderPlatforms: data.orderPlatforms || [],
  //   ratingTotal: data.ratingTotal || 0,
  //   ratingCount: data.ratingCount || 0,
  //   // add more public fields if needed
  // };
}

export async function addServiceOfferingClient({serviceId, details}) {
  const serviceRef = doc(db, 'services', serviceId);
  await updateDoc(serviceRef, {
    offerings: arrayUnion(details)
  });
}

export async function addServiceCategoryClient({serviceId, details}) {
  const serviceRef = doc(db, 'services', serviceId);
  await updateDoc(serviceRef, {
    storeCategories: arrayUnion(details)
  });
}

export async function addCategoryItemsClient({serviceId, categoryIndex, items}) {
  const serviceRef = doc(db, 'services', serviceId);
  const serviceSnap = await getDoc(serviceRef);
  
  if (!serviceSnap.exists()) {
    throw new Error('Service not found');
  }
  
  const serviceData = serviceSnap.data();
  const categories = serviceData.storeCategories || [];
  
  if (categoryIndex >= categories.length) {
    throw new Error('Category not found');
  }
  
  const category = categories[categoryIndex];
  const currentRegion = serviceData.region || 'default';
  
  if (!category.itemsByRegion) {
    category.itemsByRegion = {};
  }
  if (!category.itemsByRegion[currentRegion]) {
    category.itemsByRegion[currentRegion] = [];
  }
  
  category.itemsByRegion[currentRegion].push(...items);
  categories[categoryIndex] = category;
  
  await updateDoc(serviceRef, { storeCategories: categories });
}

export async function getServiceBySlugClient({ serviceSlug, itemSlug }) {
  const servicesRef = collection(db, 'services');
  const serviceQuery = query(servicesRef, where('serviceSlug', '==', serviceSlug));
  const serviceSnapshot = await getDocs(serviceQuery);
  
  if (serviceSnapshot.empty) {
    throw new Error('Service not found');
  }
  
  const serviceDoc = serviceSnapshot.docs[0];
  const serviceId = serviceDoc.id;
  
  const result = await getServiceWithItemsClient({ serviceId });
  const { service, items } = result;
  
  const foundItem = items.find(i => i.itemSlug === itemSlug || i.name.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '-') === itemSlug);
  
  if (!foundItem) {
    throw new Error('Item not found');
  }
  
  return { service, serviceId, item: foundItem };
}

export async function getServiceItemDetailsClient({ serviceId, itemName }) {
  const itemsRef = collection(db, 'services', serviceId, 'servicesAndPrices');
  const snapshot = await getDocs(itemsRef);
  const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  const foundItem = items.find(i => i.name === itemName);
  return foundItem || null;
}
